import React, { FC, useEffect, useState } from 'react';

import { Fab, Hidden } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';

import { IAdminFlight } from '../../../../../../../store/admin/eventFlights/store';

import Select from '../../../../../../../components/AutoComplete';
import { Input } from '../../../../../../../components/Input';
import { genderOptions } from '../../../../../../../contrib/Utils/genderOptions';
import { isFlightInvalid } from '../Utils/isFlightValid';
import { getFormattedHandicapValue } from '../Utils/getFormattedHandicapValue';
import { FlightRowStyled } from '../FlightRow.Styles';

interface IProps {
  automaticHandicaps: boolean;
  deleteFlight: (idx: number, flight: IAdminFlight) => () => Promise<any> | void;
  flight: IAdminFlight;
  flightsByAge: boolean;
  flightsByHandicap: boolean;
  flightSetup: (field: string, index: number) => (event: any) => void;
  flightsLength?: number;
  index: number;
  saveFlight: (index: number, flight: IAdminFlight) => () => Promise<any>;
  unsavedFlights: number[];
}

const FlightRow: FC<IProps> = props => {
  const {
    automaticHandicaps,
    deleteFlight,
    flight,
    flightSetup,
    flightsLength,
    index,
    saveFlight,
    flightsByAge,
    flightsByHandicap,
    unsavedFlights
  } = props;

  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  const getHandicapValue = (handicapField: 'handicapLower' | 'handicapUpper') => {
    const handicapValue = flight[handicapField];

    return getFormattedHandicapValue(handicapValue);
  };

  useEffect(() => {
    const flightIsUnsaved = unsavedFlights.indexOf(index) < 0;

    const flightIsInvalid = isFlightInvalid(
      flight,
      flightsByAge,
      flightsByHandicap,
      automaticHandicaps
    );

    setIsSaveDisabled(flightIsUnsaved || flightIsInvalid);
  }, [unsavedFlights, index, flight, flightsByAge, flightsByHandicap, automaticHandicaps]);

  return (
    <FlightRowStyled className="flight-rows items">
      <div className="sub-rows name">
        <Hidden mdUp={true}>
          <label>NAME</label>
        </Hidden>

        <div className="sub-rows inner">
          <Input
            classes={{ root: 'flights-input' }}
            errorMessage="Flight must have a name"
            fullWidth={true}
            hasError={flight.name === ''}
            id={`flight-name-${index}`}
            label=""
            multiline={false}
            onChange={flightSetup('name', index)}
            type="text"
            value={flight.name || ''}
          />
        </div>
      </div>

      <div className="sub-rows sex">
        <Hidden mdUp={true}>
          <label>SEX</label>
        </Hidden>

        <div className="sub-rows inner">
          <Select
            value={
              flight.gender === null
                ? genderOptions.find(option => option.value === 'B')
                : genderOptions.find(option => option.value === flight.gender)
            }
            onChange={flightSetup('gender', index)}
            color="primary"
            options={genderOptions}
          />
        </div>
      </div>

      {flightsByAge && (
        <div className="sub-rows age">
          <Hidden mdUp={true}>
            <label>AGE</label>
          </Hidden>

          <div className="sub-rows inner">
            <Input
              classes={{ root: 'flights-input' }}
              id={`age-${index}-lower`}
              inputProps={{ min: 0 }}
              fullWidth={true}
              type="number"
              value={flight.ageLower || ''}
              onChange={flightSetup('ageLower', index)}
              label="LOWEST"
            />

            <div className="table-title to-field">TO</div>

            <Input
              classes={{ root: 'flights-input' }}
              fullWidth={true}
              id={`age-${index}-upper`}
              inputProps={{ min: 0 }}
              onChange={flightSetup('ageUpper', index)}
              type="number"
              value={flight.ageUpper || ''}
              label="HIGHEST"
            />
          </div>
        </div>
      )}

      {flightsByHandicap && (
        <div className="sub-rows handicaps">
          <Hidden mdUp={true}>
            <label>HANDICAPS</label>
          </Hidden>

          {automaticHandicaps ? (
            <i>Automatically Defined</i>
          ) : (
            <div className="sub-rows inner">
              <Input
                classes={{ root: 'flights-input' }}
                fullWidth={true}
                id={`hc-${index}-lower`}
                label="LOWEST"
                type="handicap"
                value={getHandicapValue('handicapLower')}
                onChange={flightSetup('handicapLower', index)}
              />

              <div className="table-title to-field">TO</div>

              <Input
                classes={{ root: 'flights-input' }}
                fullWidth={true}
                id={`hc-${index}-upper`}
                type="handicap"
                value={getHandicapValue('handicapUpper')}
                onChange={flightSetup('handicapUpper', index)}
                label="HIGHEST"
              />
            </div>
          )}
        </div>
      )}

      <div className="sub-rows sub-row-actions">
        <Fab
          aria-label="Save Flight"
          className="edit-button"
          color="primary"
          disabled={isSaveDisabled}
          onClick={saveFlight(index, flight)}
          size="small"
        >
          <SaveIcon />
        </Fab>

        <Fab
          aria-label="Delete Flight"
          color="primary"
          disabled={flightsLength === 1}
          onClick={deleteFlight(index, flight)}
          size="small"
        >
          <DeleteIcon />
        </Fab>
      </div>
    </FlightRowStyled>
  );
};

export default FlightRow;
